import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';

import {
  ExportTable,
  FormatDate,
  CapitalizeFirstLetter,
  SortByLatest,
} from '../../utils/Functions';
import Table from '../Table';
import InfoPanel from '../Panels/InfoPanel';
import IziToast from '../IziToast';

const ReceiptsTable = ({ isLoading, error, receipts, user }) => {
  // ── Send Receipt channel-picker modal state ──────────────────────────
  // Tenants occasionally request a copy of an old receipt — this modal
  // mirrors the Send Invoice flow (Email / SMS / WhatsApp checkboxes +
  // Send / Cancel). Default channels: Email + SMS.
  const [resendRow, setResendRow] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    ExportTable('receipts_table', 'receipts');
  }, [receipts, isLoading]);

  const openResendModal = (row) => {
    setResendRow(row);
    setSendEmail(true);
    setSendSms(true);
    setSendWhatsapp(false);
  };

  const closeResendModal = () => {
    if (sendingId) return;
    setResendRow(null);
  };

  const submitResend = async () => {
    const row = resendRow;
    if (!row) return;
    if (!sendEmail && !sendSms && !sendWhatsapp) {
      IziToast('Pick at least one channel');
      return;
    }
    setSendingId(row.id);
    const picked = [];
    if (sendEmail) picked.push('email');
    if (sendSms) picked.push('SMS');
    if (sendWhatsapp) picked.push('WhatsApp');
    try {
      const userToken = user && user.token;
      await axios.post(
        `${process.env.REACT_APP_API_URL}payments/resend-receipt`,
        {
          id: row.id,
          send_email: sendEmail,
          send_sms: sendSms,
          send_whatsapp: sendWhatsapp,
        },
        { headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' } },
      );
      IziToast(`Receipt sent via ${picked.join(', ')}`);
      setResendRow(null);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to send receipt');
    }
    setSendingId(null);
  };

  return isLoading ? (
    <InfoPanel message="Loading..." />
  ) : error ? (
    <InfoPanel message="An error occurred" hasError />
  ) : (
    <>
      {/* ── Resend Receipt channel-picker modal ──────────────────────── */}
      {resendRow && (
        <div
          onClick={closeResendModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              width: 'min(420px, 92vw)',
              borderRadius: 6,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                Send receipt
                {resendRow.tenant ? ` to ${resendRow.tenant.first_name || ''} ${resendRow.tenant.last_name || ''}`.trimEnd() : ''}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {`RCT-${String(resendRow.id).substring(0, 10).toUpperCase()} · pick the channels.`}
              </div>
            </div>
            <div style={{ padding: '14px 20px' }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send Email
              </label>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send SMS
              </label>
              <label style={{ display: 'block', fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendWhatsapp}
                  onChange={(e) => setSendWhatsapp(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send WhatsApp
              </label>
            </div>
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={closeResendModal}
                disabled={sendingId !== null}
                style={{
                  border: '1px solid #ccc',
                  background: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: sendingId !== null ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitResend}
                disabled={sendingId !== null}
                style={{
                  border: '1px solid var(--red_color, #d63835)',
                  background: 'var(--red_color, #d63835)',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: sendingId !== null ? 'not-allowed' : 'pointer',
                }}
              >
                {sendingId !== null ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receipts && receipts.length ? (
        <Table tableId="receipts_table" marginTop={10}>
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Property</th>
              <th>Hse. Unit</th>
              <th>Name</th>
              <th>Amount</th>
              <th>Receipt No.</th>
              <th>Particular</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {SortByLatest(receipts).map((receipt, id) => {
              let index = id;
              let propertyName = '';
              let unitNumber = '';
              let tenantName = '';
              let status = '';
              let transactionType = '';
              if (receipt.purpose) {
                transactionType = CapitalizeFirstLetter(receipt.purpose);
              }
              const receiptNo = `RCT-${String(receipt.id)
                .substring(0, 10)
                .toUpperCase()}`;
              if (receipt.property) {
                const { property } = receipt;
                if (property) {
                  propertyName = property.name;
                }
              }
              if (receipt.status) {
                status = CapitalizeFirstLetter(receipt.status);
              }
              if (receipt.tenant) {
                const { tenant } = receipt;
                if (tenant) {
                  tenantName = `${tenant.first_name} ${tenant.last_name}`;
                  if (tenant.other_details) {
                    unitNumber = tenant.other_details.unit_number || '';
                  }
                }
              }
              const isSendingThis = sendingId === receipt.id;
              return (
                <tr key={receipt.id}>
                  <td>{++index}</td>
                  <td>{FormatDate(receipt.createdat)}</td>
                  <td>{propertyName}</td>
                  <td>{unitNumber}</td>
                  <td>{tenantName}</td>
                  <td>{receipt.amount}</td>
                  <td>{receiptNo}</td>
                  <td>{transactionType}</td>
                  <td>{status}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-xs"
                      title="View PDF receipt"
                      style={{ color: '#d73836', minWidth: 50, marginRight: 4 }}
                      onClick={() => {
                        const ref = `RCT-${String(receipt.id).substring(0, 10).toUpperCase()}`;
                        const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                        window.open(`${base}/public/receipt.pdf?ref=${ref}`, '_blank');
                      }}
                    >
                      <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                      PDF
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-default"
                      title="Resend receipt to tenant via Email / SMS / WhatsApp"
                      disabled={isSendingThis}
                      style={{ minWidth: 110 }}
                      onClick={() => openResendModal(receipt)}
                    >
                      <i className="fa fa-paper-plane" style={{ marginRight: 4 }} />
                      {isSendingThis ? 'Sending...' : 'Send Receipt'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <InfoPanel message="Seems no receipt found" />
      )}
    </>
  );
};

ReceiptsTable.propTypes = {
  receipts: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

ReceiptsTable.defaultProps = {
  error: null,
  user: {},
};

export default connect((state) => state)(ReceiptsTable);
